export const TOTAL_WAVES = 8;
export const MAX_CASTLE_HEALTH = 4;
export const START_DISTANCE = 86;
export const BREACH_DISTANCE = 26;

const randomInt = (min, max, random = Math.random) =>
  Math.floor(random() * (max - min + 1)) + min;

const pick = (items, random = Math.random) =>
  items[Math.min(items.length - 1, Math.floor(random() * items.length))];

// Difficulty is no longer chosen up front — it climbs automatically as the
// waves progress. Early waves are gentle; later waves march faster and mix in
// tougher math so the challenge grows with the child.
export const approachRateForWave = (wave) => {
  const w = Math.max(1, Number(wave) || 1);
  return Math.min(0.6, 0.26 + (w - 1) * 0.045);
};

const addition = (max, random) => {
  const a = randomInt(2, Math.max(3, Math.floor(max * 0.65)), random);
  const b = randomInt(1, Math.max(2, max - a), random);
  return { prompt: `${a} + ${b}`, answer: a + b, operation: "addition" };
};

const subtraction = (max, random) => {
  const a = randomInt(4, max, random);
  const b = randomInt(1, a - 1, random);
  return { prompt: `${a} − ${b}`, answer: a - b, operation: "subtraction" };
};

const multiplication = (maxFactor, random) => {
  const a = randomInt(2, maxFactor, random);
  const b = randomInt(2, maxFactor, random);
  return { prompt: `${a} × ${b}`, answer: a * b, operation: "multiplication" };
};

const division = (maxFactor, random) => {
  const divisor = randomInt(2, maxFactor, random);
  const answer = randomInt(2, maxFactor, random);
  return { prompt: `${divisor * answer} ÷ ${divisor}`, answer, operation: "division" };
};

export const buildAnswerOptions = (answer, random = Math.random) => {
  const values = new Set([answer]);
  const offsets = [-3, -2, -1, 1, 2, 3, 4, 5];
  let guard = 0;
  while (values.size < 4 && guard < 30) {
    const offset = pick(offsets, random);
    values.add(Math.max(0, answer + offset));
    guard += 1;
  }
  while (values.size < 4) values.add(answer + values.size);

  return Array.from(values)
    .map((value) => ({ value, order: random() }))
    .sort((a, b) => a.order - b.order)
    .map(({ value }) => value);
};

// Difficulty climbs one notch every wave, matching the iOS
// MathCastleProblemFactory: gentle addition on every wave, subtraction joins
// at wave 2, multiplication at wave 4, and division at wave 6. The numbers
// grow steadily with the wave count.
export const generateCastleProblem = (wave = 1, random = Math.random) => {
  const w = Math.max(1, Number(wave) || 1);
  const sumMax = Math.min(40, 6 + w * 4);
  const factorMax = Math.min(9, Math.max(3, w - 1));

  const generators = [() => addition(sumMax, random)];
  if (w >= 2) generators.push(() => subtraction(sumMax, random));
  if (w >= 4) generators.push(() => multiplication(factorMax, random));
  if (w >= 6) generators.push(() => division(factorMax, random));

  const problem = pick(generators, random)();

  return {
    ...problem,
    options: buildAnswerOptions(problem.answer, random),
  };
};

const MONSTERS = [
  { kind: "slime", name: "Gloop", color: "#77C74F" },
  { kind: "cyclops", name: "Blinky", color: "#9C72E8" },
  { kind: "mushroom", name: "Mushboom", color: "#F06C63" },
  { kind: "dragon", name: "Sparky", color: "#4CB3A6" },
];

export const monsterForWave = (wave) => {
  const w = Math.max(1, Number(wave) || 1);
  const index = (w - 1) % MONSTERS.length;
  const health = Math.min(3, 1 + Math.floor((w - 1) / 3));
  return { ...MONSTERS[index], health, maxHealth: health };
};

export const pointsForAnswer = ({ combo = 0, distance = 0 } = {}) =>
  90 + Math.min(120, Math.round(distance)) + Math.min(100, combo * 20);

export const healthAfterCorrectAnswer = (health) =>
  Math.max(0, Number(health || 0) - 1);
